'use client';

import { useEffect, useState } from 'react';
import { apiService } from '@/lib/api';
import { rashiSyllables, rashiName } from '@/lib/rashiData';
import { Star, Loader2, X, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';

type ZodiacSign = {
  id: number;
  englishName: string;
  nepaliName: string;
  nepaliNameRomanized: string;
  symbol: string;
  element: string;
  planet: string;
  startDate: string;
  endDate: string;
  color?: string;
  icon: string;
};

// Four elemental colors (Fire/Earth/Air/Water), not twelve random hues — a real
// astrological distinction, rooted in the brand palette rather than a rainbow.
const ELEMENT_COLORS: Record<string, string> = {
  Fire:  'from-primary-500 to-primary-700',   // brand maroon
  Earth: 'from-accent-500 to-accent-700',     // brand gold
  Air:   'from-slate-400 to-slate-600',
  Water: 'from-sky-600 to-primary-800',
};
const zodiacColors = [ELEMENT_COLORS.Fire, ELEMENT_COLORS.Earth, ELEMENT_COLORS.Air, ELEMENT_COLORS.Water];

// UI language — English / Nepali / Hindi
type Lang = 'en' | 'ne' | 'hi';
const LANG_TO_API: Record<Lang, 'english' | 'nepali' | 'hindi'> = { en: 'english', ne: 'nepali', hi: 'hindi' };

// Translation object
const translations: Record<Lang, { element: string; rulingPlanet: string; daily: string; weekly: string; yearly: string }> = {
  en: {
    element: 'Element',
    rulingPlanet: 'Ruling Planet',
    daily: 'Daily',
    weekly: 'Weekly',
    yearly: 'Yearly',
  },
  ne: {
    element: 'तत्व',
    rulingPlanet: 'शासक ग्रह',
    daily: 'दैनिक',
    weekly: 'साप्ताहिक',
    yearly: 'वार्षिक',
  },
  hi: {
    element: 'तत्व',
    rulingPlanet: 'स्वामी ग्रह',
    daily: 'दैनिक',
    weekly: 'साप्ताहिक',
    yearly: 'वार्षिक',
  },
};

// Element translations
const elementTranslations: Record<string, Record<Lang, string>> = {
  'Fire': { en: 'Fire', ne: 'अग्नि', hi: 'अग्नि' },
  'Earth': { en: 'Earth', ne: 'पृथ्वी', hi: 'पृथ्वी' },
  'Air': { en: 'Air', ne: 'वायु', hi: 'वायु' },
  'Water': { en: 'Water', ne: 'जल', hi: 'जल' },
};

// Planet translations
const planetTranslations: Record<string, Record<Lang, string>> = {
  'Mars': { en: 'Mars', ne: 'मंगल', hi: 'मंगल' },
  'Venus': { en: 'Venus', ne: 'शुक्र', hi: 'शुक्र' },
  'Mercury': { en: 'Mercury', ne: 'बुध', hi: 'बुध' },
  'Moon': { en: 'Moon', ne: 'चंद्र', hi: 'चंद्र' },
  'Sun': { en: 'Sun', ne: 'सूर्य', hi: 'सूर्य' },
  'Jupiter': { en: 'Jupiter', ne: 'बृहस्पति', hi: 'बृहस्पति' },
  'Saturn': { en: 'Saturn', ne: 'शनि', hi: 'शनि' },
};

// Helper function to translate element
const translateElement = (element: string, language: Lang): string => {
  if (!element) return '';
  const translation = elementTranslations[element];
  return translation ? translation[language] : element;
};

// Helper function to translate planet
const translatePlanet = (planet: string, language: Lang): string => {
  if (!planet) return '';
  const translation = planetTranslations[planet];
  return translation ? translation[language] : planet;
};

const normalizeNepaliRashiName = (name: string): string => {
  const trimmed = (name || '').trim();
  return trimmed === 'वृषभ' ? 'वृष' : trimmed;
};

export default function RasiPhalSection() {
  const [zodiacSigns, setZodiacSigns] = useState<ZodiacSign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSign, setSelectedSign] = useState<ZodiacSign | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [language, setLanguage] = useState<Lang>('en');
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily');
  const [aggregate, setAggregate] = useState<any>(null);
  const [rasiphalLoading, setRasiphalLoading] = useState(false);

  useEffect(() => { loadZodiacSigns(); }, []);

  // Fetch the aggregated multi-topic prediction for the selected sign
  useEffect(() => {
    if (!selectedSign) { setAggregate(null); return; }
    let cancelled = false;
    (async () => {
      setRasiphalLoading(true);
      try {
        const r: any = await apiService.getRasiphalAggregate(selectedSign.id, period, LANG_TO_API[language]);
        if (!cancelled && r?.success) setAggregate(r.data || null);
      } catch { if (!cancelled) setAggregate(null); }
      finally { if (!cancelled) setRasiphalLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedSign, period, language]);

  // Dispatch custom event to notify parent about flip state changes
  useEffect(() => {
    const event = new CustomEvent('rasiphal-flip', { 
      detail: { isFlipped } 
    });
    window.dispatchEvent(event);
  }, [isFlipped]);

  const loadZodiacSigns = async () => {
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.getZodiacSigns();
      
      if (response?.success && Array.isArray(response.data)) {
        // Map API response to our ZodiacSign type
        const mapped = response.data.map((item: any) => ({
          id: item.id,
          englishName: item.english_name || item.englishName,
          nepaliName: normalizeNepaliRashiName(item.nepali_name || item.nepaliName || ''),
          nepaliNameRomanized: item.romanized_nepali_name || item.nepaliNameRomanized,
          symbol: item.symbol,
          element: item.element || '',
          planet: item.planet || '',
          startDate: item.start_date || item.startDate || '',
          endDate: item.end_date || item.endDate || '',
          color: item.color || '',
          icon: item.icon || ''
        }));
        setZodiacSigns(mapped);
      } else {
        setError('Failed to load zodiac signs');
      }
    } catch (err: any) {
      console.error('Error loading zodiac signs:', err);
      setError(err.message || 'Failed to load zodiac signs');
      // Use fallback data if API fails
      setZodiacSigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSignClick = (sign: ZodiacSign) => {
    setSelectedSign(sign);
    setIsFlipped(true);
    // Reset period to daily when selecting a new sign
    setPeriod('daily');
  };

  const handleCloseCard = () => {
    setIsFlipped(false);
    setSelectedSign(null);
  };

  // Navigate to next zodiac sign
  const handleNextSign = () => {
    if (!selectedSign) return;

    const currentIndex = signsToDisplay.findIndex(sign => sign.id === selectedSign.id);
    const nextIndex = (currentIndex + 1) % signsToDisplay.length; // Cycle to first if at last
    const nextSign = signsToDisplay[nextIndex];

    if (nextSign) {
      setSelectedSign(nextSign);
      setPeriod('daily'); // Reset period to daily when navigating
    }
  };

  // Navigate to previous zodiac sign
  const handlePreviousSign = () => {
    if (!selectedSign) return;

    const currentIndex = signsToDisplay.findIndex(sign => sign.id === selectedSign.id);
    const prevIndex = currentIndex === 0 ? signsToDisplay.length - 1 : currentIndex - 1; // Cycle to last if at first
    const prevSign = signsToDisplay[prevIndex];

    if (prevSign) {
      setSelectedSign(prevSign);
      setPeriod('daily'); // Reset period to daily when navigating
    }
  };

  // Fallback data if API fails
  const fallbackSigns: ZodiacSign[] = [
    { id: 1, englishName: 'Aries', nepaliName: 'मेष', nepaliNameRomanized: 'Mesh', symbol: '♈', element: 'Fire', planet: 'Mars', startDate: 'March 21', endDate: 'April 19', icon: 'ram' },
    { id: 2, englishName: 'Taurus', nepaliName: 'वृष', nepaliNameRomanized: 'Vrishabha', symbol: '♉', element: 'Earth', planet: 'Venus', startDate: 'April 20', endDate: 'May 20', icon: 'bull' },
    { id: 3, englishName: 'Gemini', nepaliName: 'मिथुन', nepaliNameRomanized: 'Mithun', symbol: '♊', element: 'Air', planet: 'Mercury', startDate: 'May 21', endDate: 'June 20', icon: 'twins' },
    { id: 4, englishName: 'Cancer', nepaliName: 'कर्कट', nepaliNameRomanized: 'Karkat', symbol: '♋', element: 'Water', planet: 'Moon', startDate: 'June 21', endDate: 'July 22', icon: 'crab' },
    { id: 5, englishName: 'Leo', nepaliName: 'सिंह', nepaliNameRomanized: 'Singh', symbol: '♌', element: 'Fire', planet: 'Sun', startDate: 'July 23', endDate: 'August 22', icon: 'lion' },
    { id: 6, englishName: 'Virgo', nepaliName: 'कन्या', nepaliNameRomanized: 'Kanya', symbol: '♍', element: 'Earth', planet: 'Mercury', startDate: 'August 23', endDate: 'September 22', icon: 'virgin' },
    { id: 7, englishName: 'Libra', nepaliName: 'तुला', nepaliNameRomanized: 'Tula', symbol: '♎', element: 'Air', planet: 'Venus', startDate: 'September 23', endDate: 'October 22', icon: 'scales' },
    { id: 8, englishName: 'Scorpio', nepaliName: 'वृश्चिक', nepaliNameRomanized: 'Vrishchik', symbol: '♏', element: 'Water', planet: 'Mars', startDate: 'October 23', endDate: 'November 21', icon: 'scorpion' },
    { id: 9, englishName: 'Sagittarius', nepaliName: 'धनु', nepaliNameRomanized: 'Dhanu', symbol: '♐', element: 'Fire', planet: 'Jupiter', startDate: 'November 22', endDate: 'December 21', icon: 'archer' },
    { id: 10, englishName: 'Capricorn', nepaliName: 'मकर', nepaliNameRomanized: 'Makar', symbol: '♑', element: 'Earth', planet: 'Saturn', startDate: 'December 22', endDate: 'January 19', icon: 'goat' },
    { id: 11, englishName: 'Aquarius', nepaliName: 'कुम्भ', nepaliNameRomanized: 'Kumbh', symbol: '♒', element: 'Air', planet: 'Saturn', startDate: 'January 20', endDate: 'February 18', icon: 'water-bearer' },
    { id: 12, englishName: 'Pisces', nepaliName: 'मीन', nepaliNameRomanized: 'Meen', symbol: '♓', element: 'Water', planet: 'Jupiter', startDate: 'February 19', endDate: 'March 20', icon: 'fish' },
  ];

  const signsToDisplay = zodiacSigns.length > 0 ? zodiacSigns : fallbackSigns;

  return (
    <section
      id="rasiphal-section"
      className={`pt-6 sm:pt-8 lg:pt-10 pb-10 sm:pb-12 lg:pb-14 bg-gray-50 dark:bg-dark-900 w-full relative overflow-visible transition-all duration-500`}
      style={{ contentVisibility: 'auto', containIntrinsicSize: '460px', display: 'block', visibility: 'visible', position: 'relative', zIndex: 1 }}
      data-flipped={isFlipped}
    >
        <div className="container w-full max-w-7xl mx-auto px-3 sm:px-4 relative overflow-visible" style={{ overflow: 'visible' }}>
          {/* Flip Card Container with 3D perspective */}
          <div 
            className={`relative w-full transition-all duration-500 h-auto overflow-visible ${isFlipped ? 'min-h-[400px] sm:min-h-[450px] lg:min-h-[500px]' : ''}`}
            style={{ 
              perspective: '1000px',
              overflow: 'visible'
            }}
          >
          {/* Front Side - Zodiac Grid */}
          <div 
            className={`relative transition-all duration-500 ease-in-out h-full ${isFlipped ? 'opacity-0 pointer-events-none absolute inset-0' : 'opacity-100 pointer-events-auto'}`}
            style={{
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              backfaceVisibility: 'hidden',
              transformStyle: 'preserve-3d'
            }}
          >
            <div className="text-center mb-4 sm:mb-6 md:mb-8 px-4" style={{ display: 'block', visibility: 'visible' }}>
              <h2 className="text-xl sm:text-2xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2" style={{ display: 'block', visibility: 'visible', lineHeight: '1.4' }}>
                <span className="devanagari inline-block" style={{ lineHeight: '1.5', wordBreak: 'keep-all' }}>Rasi Phal (राशि फल)</span>
              </h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Click on your zodiac sign to discover your horoscope predictions
              </p>
              {/* Crawlable entry point — the flip card itself is JS-only, so this
                  link is how the indexable /rashifal pages get discovered. */}
              <a
                href="/rashifal"
                className="inline-block mt-2 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline devanagari"
              >
                आजको राशिफल — Read Today&apos;s Full Rashifal →
              </a>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : error && zodiacSigns.length === 0 ? (
              <div className="text-center py-8 text-red-600 dark:text-red-400">
                {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6 gap-1.5 sm:gap-2 md:gap-3 w-full max-w-full pb-4 sm:pb-4 lg:pb-6">
                {signsToDisplay.map((sign, index) => {
                  const colorClass = sign.color || ELEMENT_COLORS[sign.element] || zodiacColors[index % zodiacColors.length];
                  return (
                    <button
                      key={sign.id}
                      onClick={() => handleSignClick(sign)}
                      className="group relative text-center w-full transition-transform duration-200 cursor-pointer overflow-visible p-1.5 sm:p-2 bg-white dark:bg-dark-800 rounded-md border border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400 shadow-sm hover:shadow-md"
                    >
                      <div className="relative mb-1 sm:mb-1.5">
                        <div className={`w-7 h-7 sm:w-10 sm:h-10 md:w-14 md:h-14 mx-auto rounded-full bg-gradient-to-br ${colorClass} flex items-center justify-center text-white text-xs sm:text-sm md:text-xl font-bold shadow-lg group-hover:shadow-xl transition-all duration-200 group-hover:scale-105 ring-2 ring-white`}>
                          {sign.symbol}
                        </div>
                      </div>
                      
                      <h3 className="text-[9px] sm:text-xs md:text-sm font-bold text-gray-950 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200 mb-0.5 truncate leading-tight opacity-100">
                        {typeof sign.englishName === 'string' ? sign.englishName : String(sign.englishName || '')}
                      </h3>
                      
                      <p className="text-[8px] sm:text-[9px] md:text-xs text-gray-950 dark:text-gray-100 font-semibold truncate leading-tight opacity-100">
                        {typeof sign.nepaliName === 'string' ? sign.nepaliName : String(sign.nepaliName || '')}
                      </p>
                      <p className="text-[7px] sm:text-[8px] md:text-[10px] text-gray-700 dark:text-gray-300 mt-0 truncate leading-tight opacity-100 font-medium">
                        ({typeof sign.nepaliNameRomanized === 'string' ? sign.nepaliNameRomanized : String(sign.nepaliNameRomanized || '')})
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Back Side - Zodiac Details Card */}
          {selectedSign && (
            <div 
              className={`transition-all duration-500 ease-in-out absolute inset-0 ${isFlipped ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
              style={{
                transform: isFlipped ? 'rotateY(0deg)' : 'rotateY(-180deg)',
                backfaceVisibility: 'hidden',
                transformStyle: 'preserve-3d'
              }}
            >
              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 h-full overflow-y-auto overflow-x-hidden relative">
                {/* Header with Close Button */}
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-2 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                  {/* Left Section: Navigation Arrows, Zodiac Icon, Zodiac Name, Period Selector */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      {/* Previous Zodiac Sign Arrow */}
                      <button
                        onClick={handlePreviousSign}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors flex-shrink-0"
                        aria-label="Previous zodiac sign"
                        title="Previous zodiac sign"
                      >
                        <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" />
                      </button>
                      
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br ${selectedSign.color || ELEMENT_COLORS[selectedSign.element] || zodiacColors[selectedSign.id % zodiacColors.length]} flex items-center justify-center text-white text-xl sm:text-2xl md:text-3xl font-bold shadow-lg flex-shrink-0`}>
                        {selectedSign.symbol}
                      </div>
                      
                      {/* Next Zodiac Sign Arrow */}
                      <button
                        onClick={handleNextSign}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors flex-shrink-0"
                        aria-label="Next zodiac sign"
                        title="Next zodiac sign"
                      >
                        <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" />
                      </button>
                      <div className="min-w-0">
                        <h2 className="text-lg sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white truncate">
                          {typeof selectedSign.englishName === 'string' ? selectedSign.englishName : String(selectedSign.englishName || '')}
                        </h2>
                        <p className="text-base sm:text-base text-gray-600 dark:text-gray-400 truncate devanagari">
                          {rashiName(selectedSign.id, language === 'en' ? 'ne' : language) || selectedSign.nepaliName} ({selectedSign.nepaliNameRomanized || ''})
                        </p>
                        {/* Naamakshar — the sign's traditional naming syllables */}
                        {selectedSign && rashiSyllables(selectedSign.id, language) && (
                          <p className={`text-base sm:text-base text-gray-500 dark:text-gray-400 mt-2 ${language !== 'en' ? 'devanagari' : ''}`} style={{ lineHeight: '1.5' }}>
                            ({rashiSyllables(selectedSign.id, language)})
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Period selector pills */}
                    <div className="flex items-center gap-1 flex-shrink-0 bg-gray-100 dark:bg-dark-700 rounded-lg p-0.5">
                      {([
                        { key: 'daily',   ne: 'दैनिक',      en: 'Daily'   },
                        { key: 'weekly',  ne: 'साप्ताहिक', en: 'Weekly'  },
                        { key: 'monthly', ne: 'मासिक',      en: 'Monthly' },
                        { key: 'yearly',  ne: 'वार्षिक',    en: 'Yearly'  },
                      ] as const).map(({ key, ne, en }) => (
                        <button
                          key={key}
                          onClick={() => setPeriod(key)}
                          className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors devanagari ${
                            period === key
                              ? 'bg-white dark:bg-dark-800 text-primary-600 dark:text-primary-400 shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                          }`}
                        >
                          {language === 'en' ? en : ne}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Middle Section: Element and Ruling Planet Cards */}
                  <div className="flex flex-row items-center gap-2 flex-shrink-0 w-full lg:w-auto">
                    {selectedSign.element && (
                      <div className="bg-gray-50 dark:bg-dark-900 rounded-lg p-2.5 sm:p-3 md:p-3.5 flex-shrink-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600 flex-shrink-0" />
                          <h3 className={`font-semibold text-sm sm:text-base text-gray-900 dark:text-white whitespace-nowrap ${language === 'ne' ? 'devanagari' : ''}`}>
                            {translations[language].element}
                          </h3>
                        </div>
                        <p className={`text-sm sm:text-base text-gray-600 dark:text-gray-400 whitespace-nowrap ${language === 'ne' ? 'devanagari' : ''}`}>
                          {translateElement(typeof selectedSign.element === 'string' ? selectedSign.element : String(selectedSign.element || ''), language)}
                        </p>
                      </div>
                    )}
                    {selectedSign.planet && (
                      <div className="bg-gray-50 dark:bg-dark-900 rounded-lg p-2.5 sm:p-3 md:p-3.5 flex-shrink-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Star className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600 flex-shrink-0" />
                          <h3 className={`font-semibold text-sm sm:text-base text-gray-900 dark:text-white whitespace-nowrap ${language === 'ne' ? 'devanagari' : ''}`}>
                            {translations[language].rulingPlanet}
                          </h3>
                        </div>
                        <p className={`text-sm sm:text-base text-gray-600 dark:text-gray-400 whitespace-nowrap ${language === 'ne' ? 'devanagari' : ''}`}>
                          {translatePlanet(typeof selectedSign.planet === 'string' ? selectedSign.planet : String(selectedSign.planet || ''), language)}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Right Section: Language pills — English / Nepali / Hindi */}
                  <div className="flex items-center gap-3 flex-shrink-0 w-full lg:w-auto justify-end">
                    <div className="inline-flex items-center gap-0.5 bg-gray-100 dark:bg-dark-700 rounded-full p-1 border border-gray-300 dark:border-gray-600">
                      {([
                        { key: 'en', label: 'English', dev: false },
                        { key: 'ne', label: 'नेपाली',   dev: true },
                        { key: 'hi', label: 'हिन्दी',   dev: true },
                      ] as const).map(({ key, label, dev }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setLanguage(key)}
                          className={`px-3 h-8 rounded-full text-xs sm:text-sm font-medium transition-colors duration-200 ${dev ? 'devanagari' : ''} ${
                            language === key
                              ? 'bg-primary-600 text-white shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                          }`}
                          aria-label={`Switch language to ${label}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Close Button - Absolute positioned top right */}
                  <button
                    onClick={handleCloseCard}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors absolute top-4 right-4 flex-shrink-0"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>

                {/* Content */}
                {rasiphalLoading ? (
                  <div className="flex justify-center items-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                  </div>
                ) : (
                  <div className="space-y-6">

                    {/* Rasi Phal — aggregated: every topic in one view */}
                    {(() => {
                      const dev = language !== 'en' ? 'devanagari' : '';
                      const periodTitle: Record<string, Record<Lang, string>> = {
                        daily:   { ne: 'आजको राशिफल',      en: "Today's Rasi Phal",  hi: 'आज का राशिफल' },
                        weekly:  { ne: 'साप्ताहिक राशिफल', en: 'Weekly Rasi Phal',   hi: 'साप्ताहिक राशिफल' },
                        monthly: { ne: 'मासिक राशिफल',      en: 'Monthly Rasi Phal',  hi: 'मासिक राशिफल' },
                        yearly:  { ne: 'वार्षिक राशिफल',    en: 'Yearly Rasi Phal',   hi: 'वार्षिक राशिफल' },
                      };
                      const topicColors: Record<string, string> = {
                        general:        'border-l-primary-500',
                        health:         'border-l-green-500',
                        career:         'border-l-blue-500',
                        love:           'border-l-pink-500',
                        finance:        'border-l-amber-500',
                        education:      'border-l-indigo-500',
                        travel:         'border-l-cyan-500',
                        family:         'border-l-orange-500',
                        spiritual:      'border-l-purple-500',
                        'lucky-colour': 'border-l-rose-400',
                        'lucky-number': 'border-l-teal-500',
                      };
                      const topicName = (t: any) =>
                        language === 'ne' ? (t.name?.nepali || t.name?.english || '')
                        : language === 'hi' ? (t.name?.hindi || t.name?.nepali || t.name?.english || '')
                        : (t.name?.english || t.name?.nepali || '');
                      // Defensive: degrade to one swatch instead of crashing if the
                      // API ever answers with the pre-redesign single-object shape.
                      const luckyColours = (lc: any): any[] =>
                        !lc ? [] : Array.isArray(lc.colours) ? lc.colours : lc.hex ? [lc] : [];

                      const topics: any[] = aggregate?.topics || [];
                      const proseTopics = topics.filter((t) => t.text);
                      const luckyTopics = topics.filter((t) => t.lucky_number || t.lucky_colour);

                      return (
                        <div className="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 rounded-lg p-4 sm:p-6 mb-2">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                            <h3 className={`text-lg sm:text-xl font-bold text-gray-900 dark:text-white ${dev}`}>
                              {periodTitle[period][language]}
                            </h3>
                            {aggregate?.valid_from && (
                              <span className="text-[11px] text-gray-400">
                                {aggregate.valid_from === aggregate.valid_to
                                  ? aggregate.valid_from
                                  : `${aggregate.valid_from} – ${aggregate.valid_to}`}
                              </span>
                            )}
                          </div>

                          {rasiphalLoading ? (
                            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
                          ) : !aggregate || topics.length === 0 ? (
                            <p className={`text-gray-500 dark:text-gray-400 text-sm leading-relaxed ${dev}`}>
                              {language === 'ne' ? 'यस राशिको लागि राशिफल थपिएको छैन।'
                                : language === 'hi' ? 'इस राशि के लिए राशिफल अभी उपलब्ध नहीं है।'
                                : 'No predictions added for this rasi yet.'}
                            </p>
                          ) : (
                            <>
                              {/* Lucky strip: number, colour, time */}
                              {luckyTopics.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                  {luckyTopics.map((t) => (
                                    <div key={t.slug} className="flex items-center gap-2 bg-white/80 dark:bg-dark-800/80 rounded-lg px-3 py-2 shadow-sm">
                                      <span className={`text-[11px] font-semibold text-gray-500 dark:text-gray-400 devanagari`}>{topicName(t)}:</span>
                                      {t.lucky_number && (
                                        <span className="text-sm font-bold text-teal-600 dark:text-teal-400">
                                          {t.lucky_number.numbers.join(', ')}
                                        </span>
                                      )}
                                      {luckyColours(t.lucky_colour).length > 0 && (
                                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                          {luckyColours(t.lucky_colour).map((c: any, i: number) => (
                                            <span key={i} className="flex items-center gap-1.5">
                                              <span className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600 inline-block flex-shrink-0" style={{ backgroundColor: c.hex }} />
                                              <span className={dev}>
                                                {language === 'ne' ? c.ne : language === 'hi' ? c.hi : c.en}
                                              </span>
                                            </span>
                                          ))}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Topic predictions grid */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {proseTopics.map((t) => (
                                  <div
                                    key={t.slug}
                                    className={`bg-white/80 dark:bg-dark-800/80 rounded-lg p-3.5 border-l-4 ${topicColors[t.slug] || 'border-l-gray-300'} shadow-sm`}
                                  >
                                    <h4 className={`text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 devanagari`}>
                                      {topicName(t)}
                                    </h4>
                                    <p className={`text-sm text-gray-800 dark:text-gray-100 leading-relaxed ${dev}`}>{t.text}</p>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Action Buttons */}
                    <div className="flex gap-4 pt-4">
                      <button
                        onClick={handleCloseCard}
                        className="flex-1 btn btn-primary"
                      >
                        {language === 'ne' ? 'सबै राशि हेर्नुहोस्' : 'View All Signs'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </section>
  );
}

